import style from './style.m.less'
import { Modal, DropdownSelector, PrimaryButton } from '../../../../../ui'
import { FC, useEffect, useState } from 'react'
import { AddCircle } from '../../../../../../assets/icons'
import { Alert, Divider, Form, Input } from 'antd'
import {
    checkIsRuble,
    formatNumber,
    formatVolume,
    handleKeyDownNumeric,
    parseNumber,
    roundDecimal,
} from './helpers'
import { CloseOutlined } from '@ant-design/icons'
import { downloadPaymentOperationsFile } from './services'
import useRequest from '../../../../../hooks/useRequest'
import { getCalculatorTariffs } from '../../configurations'
import { operationOptions, tax } from './config'
import {
    ICalculatorModal,
    ICalculatorFields,
    ICalculatorResult,
    IOperation,
    IOperationTariff,
    IOperationResponse,
} from './types'
import ResultBlock from './ResultBlock'

const CalculatorModal: FC<ICalculatorModal> = ({ isModalVisible, setIsModalVisible }) => {
    const [calculatorForm] = Form.useForm()
    const [operationsData, setOperationsData] = useState<IOperation[]>([])
    const [error, setError] = useState<string>()
    const [result, setResult] = useState<ICalculatorResult | null>(null)
    const [tariff, setTariff] = useState<string | null>(null)
    const [operationTariffs, setOperationTariffs] = useState<IOperationTariff[]>([])
    const { fetch: fetchOperationTariffs } = useRequest(getCalculatorTariffs)
    const [isQuantity, setIsQuantity] = useState<boolean>(false)
    const [filePending, setFilePending] = useState<boolean>(false)

    useEffect(() => {
        fetchOperationTariffs({
            onSuccess: (results: IOperationResponse) => {
                setOperationTariffs(() => {
                    const next: IOperationTariff[] = []
                    for (const res of results['payment-tariffs']) {
                        next.push({
                            id: res.operation_type_id,
                            tariff: parseFloat(
                                res.qnt_price
                                    ? res.qnt_price
                                    : res.fixed_price
                                    ? res.fixed_price
                                    : '0',
                            ),
                        })
                    }
                    return next
                })
            },
        })
    }, [])

    const addOperation = (values: ICalculatorFields) => {
        resetDisplays()
        const selectedOperation = operationOptions.find((o) => o.value === values.operation)
        if (selectedOperation) {
            const tariff = operationTariffs.find((t) => t.id === selectedOperation.id)
            if (tariff) {
                const isRuble = checkIsRuble(selectedOperation.value)
                const amountNds = finRound(tariff.tariff * parseNumber(values.volume))

                setOperationsData((prev) => [
                    ...prev,
                    {
                        amount_nds: amountNds > 0.06 ? amountNds : 0.06,
                        name: selectedOperation.value,
                        tariff: tariff.tariff,
                        volume: parseNumber(values.volume),
                        is_ruble: isRuble,
                    },
                ])
                calculatorForm.resetFields()
            }
        }
    }

    const removeOperation = (idx: number) => {
        resetDisplays()
        setOperationsData((prev) => prev.filter((_, i) => i !== idx))
    }

    const resetDisplays = () => {
        setError('')
        setTariff(null)
        setResult(null)
    }

    const handleCancel = () => {
        resetDisplays()
        setOperationsData([])

        calculatorForm.resetFields()
        setIsModalVisible(false)
    }

    const handleDownload = () => {
        if (!result) {
            return
        }
        setFilePending(true)
        downloadPaymentOperationsFile({
            fee: result.fee,
            price_fee: result.priceFee,
            price: result.price,
            operations: operationsData,
        }).then(() => {
            setFilePending(false)
        })
    }

    const finRound = (number: number): number => {
        return roundDecimal(number, 2)
    }

    const handleConfirm = () => {
        if (operationsData.length === 0) {
            setError('Добавьте хотя бы одну услугу')
            return
        }

        let priceFee = 0
        operationsData.forEach((o): void => {
            priceFee += o.amount_nds
        })
        priceFee = finRound(priceFee)
        const price = finRound(priceFee / (1 + tax))
        const fee = finRound(priceFee - price)

        setResult({
            price: `${formatNumber(price.toString(), 2).replace('.', ',')} ₽`,
            fee: `${formatNumber(fee.toString(), 2).replace('.', ',')} ₽`,
            priceFee: `${formatNumber(priceFee.toString(), 2).replace('.', ',')} ₽`,
        })
    }

    const handleSelectOperation = (value: string) => {
        const selectedOperation = operationOptions.find((o) => o.value === value)
        if (selectedOperation) {
            setIsQuantity(checkIsRuble(selectedOperation.value))

            const selectedTariff = operationTariffs.find((t) => t.id === selectedOperation.id)
            if (selectedTariff) {
                setTariff(formatNumber(selectedTariff.tariff.toString(), 2, 5).replace('.', ','))
            }
        }
        return value
    }

    return (
        <Modal isModalVisible={isModalVisible} onCancel={handleCancel}>
            <div className={style.mainWrapper}>
                <div className={style.contentWrapper}>
                    <div className={style.modalHeader}>Калькулятор расчета стоимости</div>

                    <Alert
                        message={
                            <>
                                Для корректного расчета стоимости в поле "Объем атрибутов генерации,
                                кВт·ч / Количество, шт." необходимо указывать данные по каждому
                                зеленому инструменту отдельно
                            </>
                        }
                        className={style.infoAlert}
                        type="info"
                        showIcon
                    />
                    <Form
                        form={calculatorForm}
                        className={style.modalBody}
                        layout="vertical"
                        onFinish={addOperation}
                    >
                        <Form.Item
                            label={'Наименование услуги'}
                            name="operation"
                            rules={[
                                {
                                    required: true,
                                    message: 'Выберите наименование услуги',
                                },
                            ]}
                            getValueFromEvent={(value: string) => handleSelectOperation(value)}
                        >
                            <DropdownSelector
                                customStyle={{ width: '100%' }}
                                options={operationOptions}
                                noDefaultValue
                            />
                        </Form.Item>
                        {tariff && (
                            <span className={style.tariffBlock}>
                                Стоимость услуги - {tariff} ₽/{isQuantity ? 'шт.' : 'кВт·ч'}
                            </span>
                        )}
                        <Form.Item
                            label={'Объем атрибутов генерации, кВт·ч / Количество, шт.'}
                            name="volume"
                            rules={[
                                {
                                    required: true,
                                    message: 'Введите объем или количество',
                                },
                                {
                                    validator: (_, value) => {
                                        if (value === 0 || value === '0') {
                                            return Promise.reject(
                                                new Error('Введите объем или количество'),
                                            )
                                        }
                                        return Promise.resolve()
                                    },
                                },
                            ]}
                            style={{ marginTop: 16 }}
                            getValueFromEvent={(e) => formatVolume(e.target.value)}
                        >
                            <Input
                                style={{ width: '100%', borderRadius: 8 }}
                                type="text"
                                onKeyDown={handleKeyDownNumeric}
                            />
                        </Form.Item>
                        <button type="submit" className={style.addOperation}>
                            <AddCircle />
                            <div>Добавить услугу</div>
                        </button>
                        <div className={style.operationsGrid}>
                            {operationsData.map((operation, idx) => (
                                <Alert
                                    key={idx}
                                    className={style.operationAlert}
                                    message={
                                        <div className={style.alertTitle}>
                                            {`${idx + 1}. ${operation.name}`}
                                        </div>
                                    }
                                    description={
                                        <>
                                            <CloseOutlined
                                                className={style.alertCloseIcon}
                                                onClick={() => removeOperation(idx)}
                                            />
                                            <span
                                                className={style.alertDetail}
                                            >{`Стоимость услуги: ${formatNumber(
                                                operation.tariff.toString(),
                                                2,
                                                5,
                                            ).replace('.', ',')} ₽/${
                                                operation.is_ruble ? 'шт.' : 'кВт·ч'
                                            }`}</span>
                                            <Divider type="vertical" />
                                            <span className={style.alertDetail}>
                                                {operation.is_ruble
                                                    ? `Количество: ${operation.volume}`
                                                    : `Объем атрибутов генерации: ${operation.volume} кВт·ч`}
                                            </span>
                                        </>
                                    }
                                    type="info"
                                />
                            ))}
                        </div>
                        {error && <span className={style.error}>{error}</span>}
                        {result && <ResultBlock result={result} />}
                    </Form>
                    <div className={style.buttonsBlock}>
                        <PrimaryButton
                            onClick={handleCancel}
                            value={'Отмена'}
                            isCancel
                            customStyle={{ padding: '8px 16px' }}
                        />
                        {result && (
                            <PrimaryButton
                                onClick={handleDownload}
                                value={'Скачать результаты расчета'}
                                customStyle={{
                                    marginLeft: '12px',
                                    padding: '8px 16px',
                                    cursor: filePending ? 'wait' : 'pointer',
                                }}
                                disabled={filePending}
                            />
                        )}
                        <PrimaryButton
                            onClick={handleConfirm}
                            value={'Рассчитать'}
                            customStyle={{ marginLeft: '12px', padding: '8px 16px' }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default CalculatorModal
